import React, { useEffect, useState } from 'react';
import { CommonUtil } from '../../utils/CommonUtil';
import { Constants } from '../Languages/Constants';
import InputField from '../../Components/InputField';
import FlatpickrField from '../../Components/FlatpickrField';
import { NetworkManager } from '../../Components/NetworkManager';
import BusyLoader from '../../Components/BusyLoader';
import ToastManager from '../../Components/ToastManager';
import Confirm from '../../Components/confirm';
import CheckAvailabilityPreview from './CheckAvailabilityPreview';
import GenericPopup from '../../Components/GenericPopup';

interface RentalActionComponentProps {
    title: string;
    actionType: 'activation' | 'deactivation';
    reservationData: any;
    equipData: any;
    onCancel: () => void;
    onSave: () => void;
}

const RentalActionComponent = ({ title, actionType, reservationData, equipData, onCancel, onSave }: RentalActionComponentProps) => {
    const [hubodometer, setHubodometer] = useState(actionType === 'activation' ? (reservationData?.activationOdometer || "") : (reservationData?.deactivationOdometer || ""));
    const [actionDate, setActionDate] = useState(new Date());
    const [licensePlate, setLicensePlate] = useState(equipData?.licensePlateNumber || "");
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [billingPreviewData, setBillingPreviewData] = useState<any>(null);
    const [showBillingPreview, setShowBillingPreview] = useState(false);

    useEffect(() => {
        if (equipData?.licensePlateNumber) {
            setLicensePlate((prev: string) => prev || equipData.licensePlateNumber);
        }
    }, [equipData?.licensePlateNumber]);

    useEffect(() => {
        if (actionType === 'activation') {
            setHubodometer((prev: string) => prev || (reservationData?.activationOdometer || ""));
        } else {
            setHubodometer((prev: string) => prev || (reservationData?.deactivationOdometer || ""));
        }
    }, [reservationData, actionType]);

    const handleConfirm = () => {
        if (!reservationData?.reservationId) {
            ToastManager.showToast("Reservation ID not found", "Error");
            return;
        }

        let newErrors: { [key: string]: string } = {};

        if (!hubodometer) {
            newErrors.hubodometer = "Hubodometer is required";
        }
        if (!actionDate) {
            newErrors.actionDate = "Action date is required";
        }
        if (!equipData?.licensePlateNumber && !licensePlate) {
            newErrors.licensePlate = "License plate number is required";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setErrors({});

        Confirm.show("Confirm", "This action may impact the existing invoices if any. Do you want to proceed?", [
            {
                label: "Yes",
                callback: () => {
                    const payload = {
                        reservationId: reservationData.reservationId,
                        licensePlateNumber: licensePlate,
                        ...(actionType === 'activation'
                            ? { activationOdometer: hubodometer }
                            : { deactivationOdometer: hubodometer }),
                        activationDate: actionType === 'activation' ? actionDate : undefined,
                        deactivationDate: actionType === 'deactivation' ? actionDate : undefined
                    };

                    const apiPath = `api/reservation/${actionType}/${reservationData.reservationId}`;

                    BusyLoader.showLoader();
                    NetworkManager.sendJsonRequest(
                        apiPath, payload, "", "PUT", true,
                        (response: any) => {
                            const res = JSON.parse(response);
                            if (!res.errorMessage) {
                                BusyLoader.hideLoader();
                                ToastManager.showToast(`Updated successfully`, "Success");
                                onSave();
                                setHubodometer("");
                            } else {
                                BusyLoader.hideLoader();
                                ToastManager.showToast(res.errorMessage, "Error");
                            }
                        },
                        (error: any) => {
                            BusyLoader.hideLoader();
                            console.error(error);
                        }
                    );
                }
            },
            { label: "No" }
        ]);
    };

    const onPreviewInvoice = () => {
        if (!reservationData?.reservationId) {
            ToastManager.showToast("Reservation ID not found", "Error");
            return;
        }

        const payload = {
            reservationId: reservationData.reservationId,
            licensePlateNumber: licensePlate,
            hubodometer: hubodometer,
            deactivationDate: actionDate
        };

        BusyLoader.showLoader();
        NetworkManager.sendJsonRequest(
            `api/reservation/deactivation/preview/${reservationData.reservationId}`,
            payload, "", "POST", true,
            (response: any) => {
                const res = CommonUtil.parseData(response);
                BusyLoader.hideLoader();
                setBillingPreviewData(res);
                setShowBillingPreview(true);
            },
            (error: any) => {
                BusyLoader.hideLoader();
                console.error(error);
            }
        );
    };

    return (
        <div className="d-flex flex-column h-100">
            <div className="flex-grow-1 p-4 overflow-auto">
                <div className="row g-3">
                    <div className="col-12 mt-0">
                    <div className="row">
                        <section className="mb-3 h-100">
                            <h6 className="text-primary border-bottom pb-2 mb-4">Equipment Details</h6>
                            <div className="row mb-4 g-3">
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.VIN_NO, "VIN")}
                                        value={equipData?.VIN || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.BRAND, "Brand")}
                                        value={equipData?.brand || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.YEAR, "Year")}
                                        value={equipData?.year || ""}
                                        disabled
                                    />
                                </div>
                                </div>
                                <div className="row mb-4 g-3">
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.UNIT_NUMBER, "Unit Number")}
                                        value={equipData?.unitNumber || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.EQUIP_TYPE, "Equipment Type")}
                                        value={equipData?.equipmentType || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.ASSET_TYPE, "Asset Type")}
                                        value={equipData?.assetType || ""}
                                        disabled
                                    />
                                </div>
                                </div>
                                <div className="row mb-2 g-3">
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.LENGTH, "Length of Trailer")}
                                        value={equipData?.lengthOfTrailer || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.NUMBER_OF_AXLES, "Number of Axles")}
                                        value={equipData?.numberOfAxles || ""}
                                        disabled
                                    />
                                </div>
                            </div>
                        </section>
                        <section className="mb-3 h-100">
                            <h6 className="text-primary border-bottom pb-2 mb-4">Reservation Details</h6>
                            <div className="row mb-4 g-3">
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.RESERVATION_CODE, "Reservation Code")}
                                        value={reservationData?.reservationCode || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.STATUS, "Status")}
                                        value={reservationData?.reservationStatus || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.START_DATE, "Start Date")}
                                        value={reservationData?.startDate || ""}
                                        disabled
                                    />
                                </div>
                                </div>
                                <div className="row mb-2 g-3">
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.END_DATE, "End Date")}
                                        value={reservationData?.endDate || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.FROM_LOCATION, "Pickup Location")}
                                        value={reservationData?.departureParkingLotAddress || ""}
                                        disabled
                                    />
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.TO_LOCATION, "Return Location")}
                                        value={reservationData?.arrivalParkingLotAddress || ""}
                                        disabled
                                    />
                                </div>
                            </div>
                        </section>
                        <section className="mb-3">
                            <h6 className="text-primary border-bottom pb-2 mb-4">Rental Action</h6>
                            <div className="row g-3 ">
                                <div className="col-4">
                                    <InputField 
                                        placeholder={CommonUtil.getMessageText(Constants.LICENSE_PLATE, "License Plate Number")}
                                        value={licensePlate}
                                        onKeyPress={CommonUtil.restrictSpaces}
                                        onChange={(e: any) => {
                                            setLicensePlate(e.target.value);
                                            if (errors.licensePlate) setErrors(prev => ({ ...prev, licensePlate: "" }));
                                        }}
                                        disabled={!!equipData?.licensePlateNumber}
                                    />
                                    {errors.licensePlate && <div className="text-danger small ms-2 mt-1">{errors.licensePlate}</div>}
                                </div>
                                <div className="col-4">
                                    <InputField 
                                        placeholder={actionType === 'activation' ? "Hubodometer At Activation" : "Hubodometer At Deactivation"}
                                        value={hubodometer}
                                        onChange={(e: any) => {
                                            setHubodometer(e.target.value);
                                            if (errors.hubodometer) setErrors(prev => ({ ...prev, hubodometer: "" }));
                                        }}
                                        type="number"
                                    />
                                    {errors.hubodometer && <div className="text-danger small ms-2 mt-1">{errors.hubodometer}</div>}
                                </div>
                                <div className="col-4">
                                    <FlatpickrField 
                                        placeholder={actionType === 'activation' ? "Activation Date" : "Deactivation Date"}
                                        value={actionDate}
                                        onChange={(date: any) => {
                                            setActionDate(date[0]);
                                            if (errors.actionDate) setErrors(prev => ({ ...prev, actionDate: "" }));
                                        }}
                                        options={{
                                            dateFormat: "Y-m-d",
                                            allowInput: true,
                                            minDate: reservationData?.startDate,
                                            maxDate: new Date()
                                        }}
                                        className={errors.actionDate ? "is-invalid" : ""}
                                    />
                                    {errors.actionDate && <div className="text-danger small mt-1">{errors.actionDate}</div>}
                                </div>
                            </div>
                        </section>
                    </div>
                    </div>
                </div>
            </div>
            <div className="p-3 border-top bg-light mt-auto" style={{ position: 'sticky', bottom: 0, zIndex: 10, background: 'white' }}>
                <div className="d-flex justify-content-end gap-2 px-2">
                    {actionType === 'deactivation' && (
                        <button className="btn btn-info" onClick={onPreviewInvoice}>
                            {CommonUtil.getMessageText(Constants.PREVIEW_INVOICES, "Preview Invoices")}
                        </button>
                    )}
                    <button className="btn btn-secondary" onClick={() => {onCancel(); setHubodometer("");}}>
                        {CommonUtil.getMessageText(Constants.CANCEL, "Cancel")}
                    </button>
                    <button className="btn btn-primary" onClick={handleConfirm} style={{ minWidth: '100px' }}>
                        {CommonUtil.getMessageText(Constants.SAVE, "Save")}
                    </button>
                </div>
            </div>

            {showBillingPreview && (
                <GenericPopup
                    header={CommonUtil.getMessageText(Constants.PREVIEW_INVOICES, "Preview Invoices")}
                    mode={showBillingPreview}
                    onClose={() => setShowBillingPreview(false)}
                    fullscreen={true}
                >
                    <div style={{ backgroundColor: '#f5f5f5', padding: '1rem' }}>
                        <CheckAvailabilityPreview data={billingPreviewData} />
                    </div>
                </GenericPopup>
            )}
        </div>
    );
};

export default RentalActionComponent;
